import React, { FunctionComponent, MouseEventHandler, SVGProps } from 'react';
import styled, { css } from 'styled-components';

export type IconType = FunctionComponent<SVGProps<SVGSVGElement>>;
export type IconColorMode = 'legacy' | 'currentColor';

type MarginType = number | string;

interface IconColorStyleProps {
  $color?: string;
  $colorMode: IconColorMode;
  $hoverColor?: string;
}

const cursorCss = css<{ noPointer?: boolean }>`
  cursor: ${({ noPointer }) => (noPointer ? 'auto' : 'pointer')};
`;

const disabledCursorCss = css`
  cursor: not-allowed;
`;

const legacyFillCss = css<IconColorStyleProps>`
  ${({ $color, $colorMode }) =>
    $colorMode === 'legacy' &&
    css`
      fill: ${$color};
    `}
`;

const legacyStrokeCss = css<IconColorStyleProps>`
  ${({ $color, $colorMode }) =>
    $colorMode === 'legacy' &&
    css`
      stroke: ${$color};
    `}
`;

const disabledColorCss = css<IconColorStyleProps>`
  color: ${({ theme }) => theme.color.disabled};

  ${({ $colorMode, theme }) =>
    $colorMode === 'legacy' &&
    css`
      fill: ${theme.color.disabled};
    `}
`;

const disabledStrokeCss = css<IconColorStyleProps>`
  color: ${({ theme }) => theme.color.disabled};

  ${({ $colorMode, theme }) =>
    $colorMode === 'legacy' &&
    css`
      stroke: ${theme.color.disabled};
    `}
`;

const legacyHoverFillCss = css<IconColorStyleProps>`
  ${({ $colorMode, $hoverColor }) =>
    $colorMode === 'legacy' &&
    css`
      fill: ${$hoverColor};
    `}
`;

const hoverAnimationCss = css<IconColorStyleProps & { hoverScale?: boolean }>`
  ${({ $hoverColor, hoverScale }) =>
    (hoverScale || $hoverColor) &&
    css`
      transition: transform 0.2s ease, fill 0.2s ease, color 0.2s ease;

      :hover {
        color: ${$hoverColor};
        ${legacyHoverFillCss};
        transform: ${hoverScale ? 'scale(1.2)' : 'none'};
      }
    `}
`;

const IconWrapper = styled.span<{
  $color?: string;
  $colorMode: IconColorMode;
  $hoverColor?: string;
  height?: number;
  width?: number;
  background?: string;
  display?: string;
  ml?: MarginType;
  mr?: MarginType;
  mt?: MarginType;
  mb?: MarginType;
  noPointer?: boolean;
  round: boolean;
  isHovered?: boolean;
  disabled: boolean;
  hoverScale?: boolean;
}>`
  .icon g {
    ${legacyFillCss};
    ${cursorCss};
    ${hoverAnimationCss}
  }

  .icon {
    color: ${({ $color }) => $color};
    ${legacyFillCss};
    background-color: ${({ background }) => background};
    border-radius: ${({ round }) => (round ? '50%' : '2px')};
    display: ${({ display }) => display};
    ${cursorCss};
    ${hoverAnimationCss}
  }

  .icon use {
    ${legacyStrokeCss};
    background-color: ${({ background }) => background};
    border-radius: ${({ round }) => (round ? '50%' : '2px')};
    display: ${({ display }) => display};
    ${cursorCss};
    ${hoverAnimationCss}
  }

  display: ${({ display }) => display};
  height: ${({ height }) => height}px;
  width: ${({ width }) => width}px;
  margin-left: ${({ ml }) => (typeof ml === 'string' ? ml : ml + 'px')};
  margin-right: ${({ mr }) => (typeof mr === 'string' ? mr : mr + 'px')};
  margin-top: ${({ mt }) => (typeof mt === 'string' ? mt : mt + 'px')};
  margin-bottom: ${({ mb }) => (typeof mb === 'string' ? mb : mb + 'px')};
  ${cursorCss};

  ${({ disabled }) =>
    disabled &&
    css`
      ${disabledCursorCss};
      ${disabledColorCss};

      .icon,
      .icon g {
        ${disabledCursorCss};
        ${disabledColorCss};

        :hover {
          ${disabledCursorCss};
          ${disabledColorCss};
          transform: none;
        }
      }

      .icon use {
        ${disabledCursorCss};
        ${disabledStrokeCss};

        :hover {
          ${disabledCursorCss};
          ${disabledStrokeCss};
          transform: none;
        }
      }
    `}
`;

interface IconProps {
  IconComponent: IconType;
  size?: number;
  height?: number;
  width?: number;
  color?: string;
  colorMode?: IconColorMode;
  background?: string;
  ml?: MarginType;
  mr?: MarginType;
  mt?: MarginType;
  mb?: MarginType;
  display?: 'block' | 'inline' | 'flex';
  onClick?: MouseEventHandler<HTMLElement>;
  onMouseEnter?: MouseEventHandler<HTMLElement>;
  onMouseLeave?: MouseEventHandler<HTMLElement>;
  hoverFill?: string;
  noPointer?: boolean;
  round?: boolean;
  disabled?: boolean;
  'data-cy'?: string;
  hoverScale?: boolean;
}

export const Icon = ({
  IconComponent,
  size = 36,
  width,
  height,
  color,
  colorMode = 'legacy',
  background,
  display = 'inline',
  ml = 0,
  mr = 0,
  mt = 0,
  mb = 0,
  onClick,
  onMouseEnter,
  onMouseLeave,
  hoverFill,
  noPointer,
  round = false,
  disabled = false,
  'data-cy': dataCy,
  hoverScale = false,
}: IconProps) => {
  return (
    <IconWrapper
      $color={color}
      $colorMode={colorMode}
      $hoverColor={hoverFill}
      width={width ?? size}
      height={height ?? size}
      background={background}
      display={display}
      mr={mr}
      ml={ml}
      mt={mt}
      mb={mb}
      onClick={(e) => {
        !disabled && onClick?.(e);
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      noPointer={noPointer}
      round={round}
      disabled={disabled}
      hoverScale={hoverScale}
    >
      <IconComponent height={height ?? size} width={width ?? size} className={'icon'} data-cy={dataCy} />
    </IconWrapper>
  );
};
